import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'

import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    makeStyles,
    Theme,
    useTheme,
} from '@material-ui/core'
import {
    Search as SearchIcon,
    ViewAgenda as ViewAgendaIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    EventBusy as EventBusyIcon,
    OpenInNew as OpenInNewIcon,
} from '@material-ui/icons'

import { ReactComponent as ClipboardTickSBIcon } from '../../assets/icons/clipboard-tick.svg'

import { useDailyActivity } from '../../hooks/useDailyActivity'
import { useAptConfig, useCompany, useService, useUser } from '../../hooks'
import { resolveRoute, Routes } from '../../helpers'
import {
    ApartmentDeadline,
    Inspection,
    LeaseCluster,
    Schedule,
} from '../../models'
import { PropertyEvent } from '../../models/PropertyEvent'
import { ApartmentTour } from '../../models/Outreach/types'
import { RootState } from '../../store'
import {
    CalendarEventType,
    ClusterDetailFormState,
    LeaseModalFormState,
    defaultFormState,
    FormState,
} from '../ScheduleHome/types'
import { LeaseModal } from '../ScheduleHome/ViewComponents/LeaseModal/LeaseModal'
import { ClusterGroupDetail } from '../ScheduleHome/ViewComponents/ClusterGroupDetail'
import { DeadlineDetailModal } from '../ScheduleHome/ViewComponents/Deadline/DeadlineDetailModal'
import { ScheduleDetailPopover } from '../ScheduleHome/ViewComponents/ScheduleDetailPopover'
import { PropertyEventModal } from '../PropertyEvents/PropertyEventModal'
import { TourDayModal } from '../PropertyEvents/TourDayModal'
import { DailyActivityItemRow } from './DailyActivityItem'
import {
    ACTIVITY_TYPE_META,
    ACTIVITY_TYPE_COLOR,
    ActivityType,
    DayGroup,
    DailyActivityItem,
} from './types'
import './DailyActivity.css'

const BORDER = '#e6e9ed'

const useStyles = makeStyles((theme: Theme) => ({
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2),
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
    },
    titleArea: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
    },
    headerIcon: {
        width: 44,
        height: 44,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main}22, ${theme.palette.primary.main}08)`,
        color: theme.palette.primary.main,
        flexShrink: 0,
    },
    countChip: {
        backgroundColor: `${theme.palette.primary.main}14`,
        color: theme.palette.primary.main,
        fontWeight: 700,
        fontSize: '0.75rem',
        height: 24,
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
        flexWrap: 'wrap',
        padding: theme.spacing(1.5),
        backgroundColor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: 12,
    },
    weekNav: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        flexShrink: 0,
    },
    weekLabel: {
        fontWeight: 700,
        fontSize: '0.9rem',
        minWidth: 200,
        textAlign: 'center',
        color: theme.palette.text.primary,
    },
    todayButton: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        marginLeft: theme.spacing(0.5),
    },
    searchField: {
        flex: 1,
        minWidth: 180,
        maxWidth: 320,
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#f9fafb',
        },
    },
    typeFilters: {
        display: 'flex',
        gap: theme.spacing(0.75),
        flexWrap: 'wrap',
        marginBottom: theme.spacing(1.5),
        padding: theme.spacing(1, 1.5),
        backgroundColor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: 12,
    },
    typeChip: {
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 28,
        borderRadius: 20,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
    },
    resultCount: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
        fontWeight: 500,
        paddingLeft: 4,
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(8),
        color: theme.palette.text.secondary,
    },
}))

const buildDayLabel = (dateKey: string, timezone: string): string => {
    const dt = DateTime.fromISO(dateKey, { zone: timezone })
    const today = DateTime.now().setZone(timezone)
    if (dt.hasSame(today, 'day')) {
        return `Today — ${dt.toFormat('MMMM d')}`
    }
    if (dt.hasSame(today.plus({ days: 1 }), 'day')) {
        return `Tomorrow — ${dt.toFormat('MMMM d')}`
    }
    if (dt.hasSame(today.minus({ days: 1 }), 'day')) {
        return `Yesterday — ${dt.toFormat('MMMM d')}`
    }
    return dt.toFormat('EEEE, MMMM d')
}

const centerAnchor = () => {
    const popoverW = 600
    const popoverH = 750
    const top = Math.max(20, Math.round((window.innerHeight - popoverH) / 2))
    const left = Math.max(80, Math.round((window.innerWidth - popoverW) / 2))
    return { top, left }
}

const getWeekStartKey = (timezone: string, date?: DateTime) => {
    const base = (date ?? DateTime.now()).setZone(timezone).startOf('day')
    return base.startOf('week').toFormat('yyyy-MM-dd')
}

const formatWeekLabel = (weekStartKey: string, timezone: string) => {
    const start = DateTime.fromISO(weekStartKey, { zone: timezone })
    const end = start.plus({ days: 6 })
    const currentWeekStart = getWeekStartKey(timezone)
    if (weekStartKey === currentWeekStart) {
        return `This Week · ${start.toFormat('MMM d')} – ${end.toFormat(
            'MMM d',
        )}`
    }
    if (start.year === end.year) {
        return `${start.toFormat('MMM d')} – ${end.toFormat('MMM d, yyyy')}`
    }
    return `${start.toFormat('MMM d, yyyy')} – ${end.toFormat('MMM d, yyyy')}`
}

const isToday = (dateKey: string, timezone: string): boolean => {
    const dt = DateTime.fromISO(dateKey, { zone: timezone })
    const today = DateTime.now().setZone(timezone)
    return dt.hasSame(today, 'day')
}

interface WeekDay {
    key: string
    dayName: string
    dayNum: number
    isToday: boolean
    items: DailyActivityItem[]
}

export const DailyActivityHome: React.FC = () => {
    const classes = useStyles()
    const theme = useTheme()
    const history = useHistory()
    const bodyRef = useRef<HTMLDivElement>(null)
    const dayRefs = useRef<Record<string, HTMLDivElement | null>>({})
    const { userList } = useUser()
    const { serviceList } = useService({ getServiceList: true, cleanUp: true })
    const { vendorList } = useCompany({ getVendorList: true, cleanUp: true })
    const { inspectionTypeList } = useAptConfig({
        inspectionTypeList: true,
        cleanUp: true,
    })
    const inspectionList = useSelector(
        (state: RootState) => state.inspection.inspectionList,
    )

    const [weekStart, setWeekStart] = useState(() =>
        getWeekStartKey(Intl.DateTimeFormat().resolvedOptions().timeZone),
    )
    const [transitioning, setTransitioning] = useState(false)

    const {
        items,
        isLoading,
        timezone,
        workspaceUser,
        workorderClusters,
        apartmentDeadlineList,
        reload,
        createEvent,
        updateEvent,
        deleteEvent,
        draftMessage,
        sendEventMessage,
        getDeadlineDetail,
        transitionDeadline,
        incrementMessageCount,
        deleteApartmentDeadline,
        addApartmentDeadlines,
        createSchedule,
        updateSchedule,
        syncBedStatuses,
        getUnitReport,
        scheduleLoading,
    } = useDailyActivity({ weekStart })

    useEffect(() => {
        setWeekStart(getWeekStartKey(timezone))
    }, [timezone])

    const [search, setSearch] = useState('')
    const [visibleTypes, setVisibleTypes] = useState<
        Record<ActivityType, boolean>
    >(() =>
        ACTIVITY_TYPE_META.reduce((acc, meta) => {
            acc[meta.type] = true
            return acc
        }, {} as Record<ActivityType, boolean>),
    )

    const [selectedEvent, setSelectedEvent] = useState<PropertyEvent | null>(
        null,
    )
    const [eventModalOpen, setEventModalOpen] = useState(false)
    const [leaseModal, setLeaseModal] = useState<LeaseModalFormState | null>(
        null,
    )
    const [
        clusterDetail,
        setClusterDetail,
    ] = useState<ClusterDetailFormState | null>(null)
    const [formState, setFormState] = useState<FormState>(defaultFormState)
    const [
        inspectionConfirm,
        setInspectionConfirm,
    ] = useState<Inspection | null>(null)
    const [tourDayModal, setTourDayModal] = useState<{
        dateKey: string
        tours: ApartmentTour[]
    } | null>(null)

    useEffect(() => {
        setTourDayModal((prev) => {
            if (!prev) return prev
            const item = items.find(
                (activity) =>
                    activity.type === 'TOUR' && activity.date === prev.dateKey,
            )
            if (!item) return prev
            return {
                ...prev,
                tours: item.sourceData as ApartmentTour[],
            }
        })
    }, [items])

    const currentWeekStart = useMemo(() => getWeekStartKey(timezone), [
        timezone,
    ])
    const isCurrentWeek = weekStart === currentWeekStart

    const weekLabel = useMemo(() => formatWeekLabel(weekStart, timezone), [
        weekStart,
        timezone,
    ])

    const filteredItems = useMemo(() => {
        let result = items.filter((item) => visibleTypes[item.type])

        if (search.trim()) {
            const term = search.toLowerCase()
            result = result.filter(
                (item) =>
                    item.title.toLowerCase().includes(term) ||
                    (item.subtitle || '').toLowerCase().includes(term) ||
                    (item.description || '').toLowerCase().includes(term) ||
                    (item.location || '').toLowerCase().includes(term),
            )
        }

        return result
    }, [items, visibleTypes, search])

    const grouped = useMemo<DayGroup[]>(() => {
        const groups: Record<string, DailyActivityItem[]> = {}
        filteredItems.forEach((item) => {
            if (!groups[item.date]) groups[item.date] = []
            groups[item.date].push(item)
        })

        Object.values(groups).forEach((dayItems) => {
            dayItems.sort((a, b) => a.sortTime.localeCompare(b.sortTime))
        })

        const start = DateTime.fromISO(weekStart, { zone: timezone }).startOf(
            'day',
        )
        return Array.from({ length: 7 }, (_, i) => {
            const day = start.plus({ days: i })
            const key = day.toFormat('yyyy-MM-dd')
            return {
                label: buildDayLabel(key, timezone),
                sortKey: key,
                items: groups[key] ?? [],
            }
        })
    }, [filteredItems, weekStart, timezone])

    const weekDays = useMemo<WeekDay[]>(() => {
        const start = DateTime.fromISO(weekStart, { zone: timezone }).startOf(
            'day',
        )
        const allItemsByDate: Record<string, DailyActivityItem[]> = {}
        items.forEach((item) => {
            if (!allItemsByDate[item.date]) allItemsByDate[item.date] = []
            allItemsByDate[item.date].push(item)
        })

        return Array.from({ length: 7 }, (_, i) => {
            const day = start.plus({ days: i })
            const key = day.toFormat('yyyy-MM-dd')
            return {
                key,
                dayName: day.toFormat('EEE'),
                dayNum: day.day,
                isToday: isToday(key, timezone),
                items: allItemsByDate[key] ?? [],
            }
        })
    }, [weekStart, timezone, items])

    const toggleType = (type: ActivityType) => {
        setVisibleTypes((prev) => ({ ...prev, [type]: !prev[type] }))
    }

    const typeCounts = useMemo(() => {
        const counts = ACTIVITY_TYPE_META.reduce((acc, meta) => {
            acc[meta.type] = 0
            return acc
        }, {} as Record<ActivityType, number>)
        items.forEach((item) => {
            counts[item.type] += 1
        })
        return counts
    }, [items])

    const activeTypeCounts = useMemo(() => {
        return ACTIVITY_TYPE_META.filter((meta) => typeCounts[meta.type] > 0)
    }, [typeCounts])

    const animateWeekChange = useCallback((changeFn: () => void) => {
        setTransitioning(true)
        setTimeout(() => {
            changeFn()
            setTransitioning(false)
        }, 150)
    }, [])

    const goToPrevWeek = () => {
        animateWeekChange(() => {
            setWeekStart((prev) =>
                DateTime.fromISO(prev, { zone: timezone })
                    .minus({ weeks: 1 })
                    .toFormat('yyyy-MM-dd'),
            )
        })
    }

    const goToNextWeek = () => {
        animateWeekChange(() => {
            setWeekStart((prev) =>
                DateTime.fromISO(prev, { zone: timezone })
                    .plus({ weeks: 1 })
                    .toFormat('yyyy-MM-dd'),
            )
        })
    }

    const goToThisWeek = () => {
        animateWeekChange(() => {
            setWeekStart(currentWeekStart)
        })
    }

    const scrollToDay = (dateKey: string) => {
        const el = dayRefs.current[dateKey]
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }

    const navigateToScheduleDetail = (scheduleId: number) => {
        history.push(
            resolveRoute(Routes.apartmentScheduleDetail, ':id', scheduleId),
        )
    }

    const closeCalendarForms = () => {
        setFormState(defaultFormState)
        setClusterDetail(null)
    }

    const handleItemClick = (item: DailyActivityItem) => {
        switch (item.type) {
            case 'COMMUNITY_EVENT':
                setSelectedEvent(item.sourceData as PropertyEvent)
                setEventModalOpen(true)
                break
            case 'MOVE_IN':
                setLeaseModal({
                    type: CalendarEventType.MOVE_IN,
                    leaseCluster: item.sourceData as LeaseCluster,
                })
                break
            case 'MOVE_OUT':
                setLeaseModal({
                    type: CalendarEventType.MOVE_OUT,
                    leaseCluster: item.sourceData as LeaseCluster,
                })
                break
            case 'SERVICE_ORDER':
            case 'ACTION_ITEM':
            case 'SERVICE_REQUEST':
                if (item.clusterIndex != null) {
                    setClusterDetail({
                        selectedClusterIndex: item.clusterIndex,
                    })
                }
                break
            case 'DEADLINE': {
                const deadline = item.sourceData as ApartmentDeadline
                setFormState({
                    ...defaultFormState,
                    modals: {
                        ...defaultFormState.modals,
                        deadlineModal: {
                            open: true,
                            deadlineId: deadline.id,
                        },
                    },
                })
                break
            }
            case 'PROJECT': {
                const schedule = item.sourceData as Schedule
                const anchor = centerAnchor()
                setFormState({
                    ...defaultFormState,
                    meta: {
                        xPos: anchor.left,
                        yPos: anchor.top,
                        date: DateTime.now().setZone(timezone),
                    },
                    popovers: {
                        ...defaultFormState.popovers,
                        projectPopover: {
                            schedule,
                            open: true,
                        },
                    },
                })
                break
            }
            case 'INSPECTION':
                setInspectionConfirm(item.sourceData as Inspection)
                break
            case 'TOUR':
                setTourDayModal({
                    dateKey: item.date,
                    tours: item.sourceData as ApartmentTour[],
                })
                break
            default:
                break
        }
    }

    const hasActivity = grouped.some((g) => g.items.length > 0)

    const getDayDots = (dayItems: DailyActivityItem[]) => {
        const typesSeen = new Set<ActivityType>()
        dayItems.forEach((item) => typesSeen.add(item.type))
        const colors = Array.from(typesSeen)
            .slice(0, 4)
            .map((t) => ACTIVITY_TYPE_COLOR[t])
        return { colors, overflow: typesSeen.size > 4 }
    }

    return (
        <Box className="daily-activity-page">
            <Box className={classes.header}>
                <Box className={classes.titleArea}>
                    <Box className={classes.headerIcon}>
                        <ViewAgendaIcon />
                    </Box>
                    <Box>
                        <Typography variant="h5" style={{ fontWeight: 700 }}>
                            Daily Activity
                        </Typography>
                        <Typography
                            style={{ fontSize: '0.8rem', color: '#6b7280' }}
                        >
                            Everything happening at the property
                        </Typography>
                    </Box>
                    {items.length > 0 && (
                        <Chip
                            label={`${items.length} item${
                                items.length !== 1 ? 's' : ''
                            } this week`}
                            size="small"
                            className={classes.countChip}
                        />
                    )}
                </Box>

                {activeTypeCounts.length > 0 && (
                    <Box className="daily-activity-summary">
                        {activeTypeCounts.map((meta) => (
                            <Box
                                key={meta.type}
                                className="daily-activity-stat"
                                style={{
                                    backgroundColor: `${meta.color}12`,
                                    color: meta.color,
                                }}
                            >
                                <span
                                    className="daily-activity-stat-dot"
                                    style={{ backgroundColor: meta.color }}
                                />
                                {typeCounts[meta.type]} {meta.label}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            <Box className={classes.toolbar}>
                <Box className={classes.weekNav}>
                    <IconButton size="small" onClick={goToPrevWeek}>
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography className={classes.weekLabel}>
                        {weekLabel}
                    </Typography>
                    <IconButton size="small" onClick={goToNextWeek}>
                        <ChevronRightIcon />
                    </IconButton>
                    {!isCurrentWeek && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            className={classes.todayButton}
                            onClick={goToThisWeek}
                        >
                            This Week
                        </Button>
                    )}
                </Box>
                <TextField
                    className={classes.searchField}
                    placeholder="Search this week..."
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    style={{ fontSize: 20, color: '#9ca3af' }}
                                />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {/* Week strip — mini 7-day overview */}
            <Box className="daily-activity-week-strip">
                {weekDays.map((day) => {
                    const { colors, overflow } = getDayDots(day.items)
                    const dayClasses = [
                        'daily-activity-week-day',
                        day.isToday && 'daily-activity-week-day--today',
                        day.items.length > 0 &&
                            !day.isToday &&
                            'daily-activity-week-day--has-items',
                    ]
                        .filter(Boolean)
                        .join(' ')
                    return (
                        <Box
                            key={day.key}
                            className={dayClasses}
                            onClick={() => scrollToDay(day.key)}
                        >
                            <span className="daily-activity-week-day-name">
                                {day.dayName}
                            </span>
                            <span className="daily-activity-week-day-num">
                                {day.dayNum}
                            </span>
                            {day.items.length > 0 ? (
                                <>
                                    <Box className="daily-activity-week-day-dots">
                                        {colors.map((c, i) => (
                                            <span
                                                key={i}
                                                className="daily-activity-week-day-dot"
                                                style={{
                                                    backgroundColor: c,
                                                }}
                                            />
                                        ))}
                                        {overflow && (
                                            <span className="daily-activity-week-day-dot daily-activity-week-day-dot--overflow" />
                                        )}
                                    </Box>
                                    <span className="daily-activity-week-day-count">
                                        {day.items.length}
                                    </span>
                                </>
                            ) : (
                                <Box className="daily-activity-week-day-dots">
                                    <span
                                        className="daily-activity-week-day-dot"
                                        style={{
                                            backgroundColor: '#e2e8f0',
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    )
                })}
            </Box>

            <Box className={classes.typeFilters}>
                {ACTIVITY_TYPE_META.map((meta) => {
                    const active = visibleTypes[meta.type]
                    const count = typeCounts[meta.type]
                    return (
                        <Chip
                            key={meta.type}
                            label={
                                <Box
                                    component="span"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 5,
                                    }}
                                >
                                    <span
                                        style={{
                                            width: 7,
                                            height: 7,
                                            borderRadius: '50%',
                                            backgroundColor: active
                                                ? meta.color
                                                : '#d1d5db',
                                            flexShrink: 0,
                                            transition: 'background-color 0.2s',
                                        }}
                                    />
                                    <span>
                                        {meta.label}
                                        {count > 0 && (
                                            <span
                                                style={{
                                                    fontWeight: 800,
                                                    marginLeft: 3,
                                                    opacity: 0.8,
                                                }}
                                            >
                                                {count}
                                            </span>
                                        )}
                                    </span>
                                </Box>
                            }
                            size="small"
                            variant="outlined"
                            className={classes.typeChip}
                            style={
                                active
                                    ? {
                                          backgroundColor: `${meta.color}12`,
                                          color: meta.color,
                                          border: `1.5px solid ${meta.color}50`,
                                      }
                                    : {
                                          backgroundColor: '#f9fafb',
                                          color: '#9ca3af',
                                          border: `1.5px solid #e5e7eb`,
                                          opacity: 0.75,
                                      }
                            }
                            onClick={() => toggleType(meta.type)}
                        />
                    )
                })}
            </Box>

            {isLoading && items.length === 0 ? (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flex={1}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <div
                    ref={bodyRef}
                    className={`daily-activity-body ${
                        transitioning
                            ? 'daily-activity-body--transitioning'
                            : 'daily-activity-body--visible'
                    }`}
                >
                    {filteredItems.length === 0 && search ? (
                        <Box className={classes.emptyState}>
                            <SearchIcon
                                style={{
                                    fontSize: 48,
                                    marginBottom: 8,
                                    opacity: 0.3,
                                }}
                            />
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                            >
                                No activity matches &ldquo;{search}&rdquo;
                            </Typography>
                            <Typography
                                variant="caption"
                                style={{ color: '#9ca3af', marginTop: 4 }}
                            >
                                Try a different search or clear filters
                            </Typography>
                        </Box>
                    ) : !hasActivity && !search ? (
                        <Box className="daily-activity-empty-week">
                            <Box className="daily-activity-empty-week-icon">
                                <EventBusyIcon
                                    style={{
                                        fontSize: 32,
                                        color: '#9ca3af',
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="body1"
                                style={{ fontWeight: 600, color: '#374151' }}
                            >
                                Nothing scheduled this week
                            </Typography>
                            <Typography
                                variant="body2"
                                style={{ color: '#9ca3af', maxWidth: 280 }}
                            >
                                Try navigating to another week or check back
                                later for new activity.
                            </Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={goToNextWeek}
                                style={{
                                    marginTop: 8,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: 8,
                                }}
                            >
                                Next Week &rarr;
                            </Button>
                        </Box>
                    ) : (
                        grouped.map((group) => {
                            const isTodayGroup = isToday(
                                group.sortKey,
                                timezone,
                            )
                            return (
                                <div
                                    key={group.sortKey}
                                    ref={(el: HTMLDivElement | null) => {
                                        dayRefs.current[group.sortKey] = el
                                    }}
                                    className={`daily-activity-date-group${
                                        isTodayGroup
                                            ? ' daily-activity-date-group--today'
                                            : ''
                                    }`}
                                >
                                    <Typography className="daily-activity-date-label">
                                        {group.label}
                                        {group.items.length > 0 && (
                                            <span className="daily-activity-day-count">
                                                {group.items.length}
                                            </span>
                                        )}
                                    </Typography>
                                    {group.items.length === 0 ? (
                                        <Typography className="daily-activity-empty-day">
                                            No activity scheduled
                                        </Typography>
                                    ) : (
                                        group.items.map((item) => (
                                            <DailyActivityItemRow
                                                key={item.id}
                                                item={item}
                                                onClick={() =>
                                                    handleItemClick(item)
                                                }
                                            />
                                        ))
                                    )}
                                </div>
                            )
                        })
                    )}
                    <div style={{ height: 16 }} />
                </div>
            )}

            <PropertyEventModal
                open={eventModalOpen}
                onClose={() => {
                    setEventModalOpen(false)
                    setSelectedEvent(null)
                }}
                event={selectedEvent}
                initialDate={null}
                timezone={timezone}
                createEvent={createEvent}
                updateEvent={updateEvent}
                deleteEvent={deleteEvent}
                draftMessage={draftMessage}
                sendEventMessage={sendEventMessage}
                onEventUpdated={setSelectedEvent}
            />

            <LeaseModal
                open={leaseModal}
                handleClose={() => setLeaseModal(null)}
                theme={theme}
                vendorList={vendorList}
                serviceList={serviceList}
                inspectionTypeList={inspectionTypeList ?? []}
                onReloadData={reload}
            />

            <ClusterGroupDetail
                theme={theme}
                formState={clusterDetail}
                onClose={closeCalendarForms}
                workorderClusters={workorderClusters}
                navigateToScheduleDetail={navigateToScheduleDetail}
                workspaceUser={workspaceUser}
                incrementClusterIndex={(amount) => {
                    if (!clusterDetail) return
                    const newIdx = clusterDetail.selectedClusterIndex + amount
                    if (newIdx < 0 || newIdx >= workorderClusters.length) {
                        return
                    }
                    setClusterDetail({ selectedClusterIndex: newIdx })
                }}
            />

            <DeadlineDetailModal
                theme={theme}
                onClose={closeCalendarForms}
                formState={formState}
                getDeadlineDetail={getDeadlineDetail}
                transitionDeadline={transitionDeadline}
                incrementMessageCount={incrementMessageCount}
                deadlineList={apartmentDeadlineList}
                deleteApartmentDeadline={deleteApartmentDeadline}
                addApartmentDeadlines={addApartmentDeadlines}
            />

            <ScheduleDetailPopover
                formState={formState}
                onClose={closeCalendarForms}
                theme={theme}
                createSchedule={createSchedule}
                updateSchedule={updateSchedule}
                loading={scheduleLoading}
                navigateToScheduleDetail={navigateToScheduleDetail}
                workspaceUser={workspaceUser}
                syncBedStatuses={syncBedStatuses}
                getUnitReport={getUnitReport}
                inspectionList={inspectionList ?? []}
                userList={userList ?? []}
                inspectionTypeList={inspectionTypeList ?? []}
                serviceList={serviceList}
            />

            <TourDayModal
                open={tourDayModal !== null}
                onClose={() => setTourDayModal(null)}
                dateKey={tourDayModal?.dateKey ?? null}
                tours={tourDayModal?.tours ?? []}
                timezone={timezone}
            />

            <Dialog
                open={inspectionConfirm !== null}
                onClose={() => setInspectionConfirm(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    style: {
                        borderRadius: 16,
                        overflow: 'hidden',
                    },
                }}
            >
                <Box
                    style={{
                        background: '#f9fafb',
                        borderBottom: '1px solid #e5e7eb',
                        padding: '20px 24px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                    }}
                >
                    <Box
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: 12,
                            backgroundColor: '#ede9fe',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <ClipboardTickSBIcon
                            style={{ width: 22, height: 22 }}
                        />
                    </Box>
                    <Box>
                        <Typography
                            style={{
                                color: '#1f2937',
                                fontWeight: 700,
                                fontSize: '1.05rem',
                            }}
                        >
                            {inspectionConfirm?.name}
                        </Typography>
                        {inspectionConfirm?.inspection_type && (
                            <Typography
                                style={{
                                    color: '#6b7280',
                                    fontSize: '0.78rem',
                                    marginTop: 2,
                                }}
                            >
                                {inspectionConfirm.inspection_type.name}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <DialogContent style={{ padding: '20px 24px 12px' }}>
                    <Typography
                        variant="body2"
                        style={{ color: '#4b5563', lineHeight: 1.6 }}
                    >
                        This will navigate you to the inspection detail page
                        where you can view units, progress, and manage the
                        inspection.
                    </Typography>
                    {inspectionConfirm?.ui_total != null &&
                        inspectionConfirm.ui_total > 0 && (
                            <Box
                                style={{
                                    marginTop: 14,
                                    padding: '10px 14px',
                                    backgroundColor: '#f5f3ff',
                                    borderRadius: 10,
                                    border: '1px solid #e9e5ff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                }}
                            >
                                <Box
                                    style={{
                                        flex: 1,
                                        height: 6,
                                        borderRadius: 3,
                                        backgroundColor: '#e2e0f0',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        style={{
                                            width: `${Math.round(
                                                ((inspectionConfirm.ui_complete ??
                                                    0) /
                                                    inspectionConfirm.ui_total) *
                                                    100,
                                            )}%`,
                                            height: '100%',
                                            borderRadius: 3,
                                            backgroundColor: '#6366f1',
                                            transition: 'width 0.3s ease',
                                        }}
                                    />
                                </Box>
                                <Typography
                                    style={{
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        color: '#6366f1',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {inspectionConfirm.ui_complete ?? 0}/
                                    {inspectionConfirm.ui_total} complete
                                </Typography>
                            </Box>
                        )}
                </DialogContent>
                <DialogActions style={{ padding: '8px 20px 16px', gap: 8 }}>
                    <Button
                        onClick={() => setInspectionConfirm(null)}
                        style={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 8,
                            color: '#6b7280',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (inspectionConfirm) {
                                history.push(
                                    resolveRoute(
                                        Routes.inspectionDetail,
                                        ':id',
                                        inspectionConfirm.id,
                                    ),
                                )
                            }
                            setInspectionConfirm(null)
                        }}
                        variant="contained"
                        style={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 8,
                            backgroundColor: '#6366f1',
                            color: '#fff',
                            boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
                            padding: '8px 20px',
                        }}
                        endIcon={<OpenInNewIcon style={{ fontSize: 16 }} />}
                    >
                        View Inspection
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
